'use client';

import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { FinderData } from '@/lib/types';
import { EMPTY_FILTERS, type FilterState } from '@/lib/filters';
import { campusShort, monthLabel, pluralEvents } from '@/lib/format';
import { ToggleChip } from './chips';
import { CampusIcon } from './CampusIcon';
import { SheetHeader } from './SheetHeader';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  options: FinderData['options'];
  months: string[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  /** How many events the current filters produce, for the footer button */
  count: number;
  hideCampus?: boolean;
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h4 className="text-xs font-bold tracking-widest text-gray-500 uppercase">{title}</h4>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * Every filter the stock finder offered (campus, month, ministry, sign-up
 * type, featured) plus event type and program, as tap-to-toggle chips.
 * Changes apply instantly (the list behind the sheet is filtered
 * client-side), so there is no Apply button, just a count to confirm and a
 * way to start over.
 */
export function FilterSheet({
  open,
  onClose,
  options,
  months,
  filters,
  onChange,
  count,
  hideCampus = false,
}: FilterSheetProps) {
  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    onChange({ ...filters, [key]: value });

  const single = <K extends 'campus' | 'ministry' | 'program' | 'eventType'>(key: K, id: number) =>
    set(key, (filters[key] === id ? null : id) as FilterState[K]);

  const programs =
    filters.ministry !== null
      ? options.programs.filter((p) => p.ministryId === filters.ministry)
      : options.programs;

  const anySet =
    filters.campus !== null ||
    filters.ministry !== null ||
    filters.program !== null ||
    filters.eventType !== null ||
    filters.month !== null ||
    filters.signup !== 0 ||
    filters.featured ||
    filters.search.trim() !== '';

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-2xl"
      noPanelPadding
      panelClassName="bg-white"
      header={<SheetHeader title="Filters" subtitle={pluralEvents(count)} />}
      footer={
        <div className="flex items-center gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:px-8">
          <button
            type="button"
            disabled={!anySet}
            onClick={() => onChange({ ...EMPTY_FILTERS })}
            className="rounded-full px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex flex-1 items-center justify-center rounded-full bg-[#299c8f] px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
          >
            Show {pluralEvents(count)}
          </button>
        </div>
      }
    >
      <SheetPage name="main">
        <div className="flex flex-col gap-7 px-6 py-6 sm:px-8">
          {!hideCampus && (
            <Section title="Campus" hint="Church-wide events always show">
              <div className="flex flex-wrap gap-2">
                {options.campuses.map((c) => (
                  <ToggleChip
                    key={c.id}
                    active={filters.campus === c.id}
                    onClick={() => single('campus', c.id)}
                  >
                    <CampusIcon congregationId={c.id} className="h-4 w-4" />
                    {campusShort(c.name)}
                  </ToggleChip>
                ))}
              </div>
            </Section>
          )}

          {months.length > 1 && (
            <Section title="Month">
              <div className="flex flex-wrap gap-2">
                {months.map((m) => (
                  <ToggleChip
                    key={m}
                    active={filters.month === m}
                    onClick={() => set('month', filters.month === m ? null : m)}
                  >
                    {monthLabel(m)}
                  </ToggleChip>
                ))}
              </div>
            </Section>
          )}

          <Section title="Sign-up">
            <div className="flex flex-wrap gap-2">
              <ToggleChip
                active={filters.signup === 1}
                onClick={() => set('signup', filters.signup === 1 ? 0 : 1)}
              >
                Registration open
              </ToggleChip>
              <ToggleChip
                active={filters.signup === 2}
                onClick={() => set('signup', filters.signup === 2 ? 0 : 2)}
              >
                Volunteer opportunities
              </ToggleChip>
              <ToggleChip
                active={filters.featured}
                onClick={() => set('featured', !filters.featured)}
              >
                Featured
              </ToggleChip>
            </div>
          </Section>

          {options.ministries.length > 1 && (
            <Section title="Ministry">
              <div className="flex flex-wrap gap-2">
                {options.ministries.map((o) => (
                  <ToggleChip
                    key={o.id}
                    active={filters.ministry === o.id}
                    onClick={() =>
                      onChange({
                        ...filters,
                        ministry: filters.ministry === o.id ? null : o.id,
                        // A program belongs to one ministry; changing ministry resets it.
                        program: null,
                      })
                    }
                  >
                    {o.name}
                  </ToggleChip>
                ))}
              </div>
            </Section>
          )}

          {filters.ministry !== null && programs.length > 1 && (
            <Section title="Program">
              <div className="flex flex-wrap gap-2">
                {programs.map((o) => (
                  <ToggleChip
                    key={o.id}
                    active={filters.program === o.id}
                    onClick={() => single('program', o.id)}
                  >
                    {o.name}
                  </ToggleChip>
                ))}
              </div>
            </Section>
          )}

          {options.eventTypes.length > 1 && (
            <Section title="Type of event">
              <div className="flex flex-wrap gap-2">
                {options.eventTypes.map((o) => (
                  <ToggleChip
                    key={o.id}
                    active={filters.eventType === o.id}
                    onClick={() => single('eventType', o.id)}
                  >
                    {o.name}
                  </ToggleChip>
                ))}
              </div>
            </Section>
          )}
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
